
import { Icon } from "@cervval.js/client-html5-core/src/components/models/icon";
import { WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { Display, setDisplay, toggleClass, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { FLEX_CONTAINER_COLUMN } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { KeyboardInput } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { getProp, setProp } from "@cervval.js/common/src/tools/any.tools";
import { clone } from "@cervval.js/common/src/tools/object.tools";
import { anyReallyAny } from "@cervval.js/common/tools";
import { CjsDialog } from "../../components/dialog/dialog";
import { getIconFaName } from "../../components/icon/iconFa.tools";
import { IconFA6 } from "../../components/icon/tools/fontAwesome6IconList.tools";
import { CjsInputWithButton } from "../../components/inputs/inputWithButton/inputWithButton";
import { PopupPosition, PopupValidationMode, ShowPopupParams, isInModal, showPopup } from "../../components/showPopup/showPopup";
import { Picker } from "../../pickers/picker";
import { CjsEditor, EditorOptions, EditorSetValueOptions, defaultEditorOptions } from "../editor";


/** PopupContainer object */
export interface PopupContainer {
    popup: HTMLElement;
    removePopup: (mode: "close" | "cancel") => void;
}

export interface EditorWithPickerOptions extends EditorOptions {

    /** PopupMode of editor */
    popupMode: PopupValidationMode;

    /** Picker position of editor */
    pickerPosition: PopupPosition;

    /** Option of popup where picker is open */
    popupOptions?: Partial<ShowPopupParams>;

    /** Icon of picker open button icon */
    buttonIcon: Icon;

    /** Value of editor will change if value of picker has changed (popup cancel still enable) */
    enableChangeValueWithPicker: boolean;

    /** Hide input to only display button that opens popup with picker */
    hideInput: boolean;

    /** Popup container of editor */
    popupContainer?: HTMLElement;

    /** Dispatch change event even if value did not change when user picks a value */
    alwaysDispatchChangeEvent?: boolean;

    /** Display only picker, don't display inputWithButton component */
    pickerOnly?: boolean;
}

export const defaultEditorWithPickerOptions: EditorWithPickerOptions = {

    // Add disabled option
    ...defaultEditorOptions,

    /** PopupMode of editor */
    popupMode: "default",

    /** Picker position of editor */
    pickerPosition: PopupPosition.BOTTOM_END,

    /** Icon of picker open button icon */
    buttonIcon: { name: getIconFaName(IconFA6.ARROW_UP_RIGHT_FROM_SQUARE) },

    /** Value of editor will change if value of picker has changed (popup cancel still enable) */
    enableChangeValueWithPicker: false,

    /** Hide input to only display button that opens popup with picker */
    hideInput: false
};

export abstract class EditorWithPicker<T, I18n = unknown, TPicker extends Picker<T | anyReallyAny> = Picker<T | anyReallyAny>> extends CjsEditor<T, I18n> {

    /** Attributes options of editor, defined and decorated in child class */
    public abstract override readonly options: WebComponentOptions<EditorWithPickerOptions>;

    /**
     * The component used to edit the value of the input
     * Instantiated by _refreshPicker before first showPopup
     */
    protected abstract _picker?: TPicker;

    /** Picker getter */
    public get picker(): TPicker {
        if (!this._picker) {
            this._picker = this._refreshPicker();
        }
        return this._picker;
    }

    /**
     * Value of editor before opening popup
     */
    protected _beforeOpenPopupValue?: T;// TODO Align with editor with validation value type when available

    /**
     * The popup which will contains the PopupComponent to edit the input value
     */
    protected _popupContainer: PopupContainer | null = null;

    /**
     * The input with button where the value will be displayed
     */
    protected _inputWithButton = new CjsInputWithButton<string>();

    /** Get input of Editor, where user set data */
    public getInput(): CjsInputWithButton<string> {
        return this._inputWithButton;
    }

    /** Called when the shadowRoot is initialized */
    public override init(): void {

        // Add inputWithButton HTML
        this._inputWithButton.components.inputWithButtonContainer.classList.add("flex-nowrap");
        this.getContainer().append(this._inputWithButton);
        this.classList.add(FLEX_CONTAINER_COLUMN);
        // Prevent button focus during tab
        this._inputWithButton.components.button.components.button.setAttribute("tabindex", "0");

        super.init();

        this._inputWithButton.components.button.clickEvent.on((e) => this._onClickEditorBtn(e));
        this._inputWithButton.components.inputText.changeEvent.on((e) => {
            this._onInputValueChange(e);
        });

        this.getInput().addEventListener("keyup", (e) => {
            if (e.key === KeyboardInput.ENTER) {
                this._onInputValueChange(e);
            }
        });

        // Add a display for showPopup calc, without this, computed size of editor isn't defined (0,0)
        setDisplay(this, Display.INHERIT);
    }

    /** Refresh component display */
    public override refresh(): void {

        super.refresh();

        // Refresh button icon
        this._refreshButtonIcon();

        // Update displayed value from _value
        this._refreshInputValue();

        // Refresh hideInput from options
        this._refreshHideInput();

        // Refresh onlyPicker from options
        this._refreshOnlyPicker();
    }

    /** Set formCheckInput as validation form element */
    public override getValidationFormElement(): HTMLElement | HTMLElement[] {
        return this.getInput().input.input;
    }

    /** Refresh icon of button in inputWithButton */
    protected _refreshButtonIcon(): void {
        // Spread buttonIcon option into inputWithButton
        this.getInput().buttonOptions.setAndRefreshIfChanged({
            icon: this.options.buttonIcon
        });
    }

    /**
     * refresh hide input option
     */
    private _refreshHideInput(): void {
        toggleVisible(this._inputWithButton.components.inputField, !this.options.hideInput, Display.FLEX);
        toggleClass(this._inputWithButton.button, this.options.hideInput, "hide-input");
    }

    /**
     * refresh only picker option
     */
    private _refreshOnlyPicker(): void {
        toggleVisible(this._inputWithButton, !this.options.pickerOnly, Display.FLEX);
        if (this.options.pickerOnly && this._picker) {
            if (!getProp(this._picker, "editorValueChangeEvent")) {
                setProp(this._picker, "editorValueChangeEvent", true);
                this._picker.valueChangeEvent.on(() => {
                    this._setValueIfDifferentAndDispatchEvents(this._getPickerValue());
                });
            }
            this.append(this._picker);
        } else {// remove picker when option "pickerOnly" change
            this._picker?.remove();
        }
    }

    /** 
     * Set editor value 
     * @returns true if value changed
     */
    public override setValue(value: T | undefined, options: EditorSetValueOptions = { dispatchEvents: true, refreshIfChanged: true }): boolean {

        if (this._value !== value || (options.dispatchEvents && this.options.alwaysDispatchChangeEvent)) {
            this._value = value;

            // do not need this._refreshInputValue(), it will be done in this.refresh()

            if (options.refreshIfChanged) {
                this.refresh();
            }

            if (options.dispatchEvents) {
                this._dispatchValueChangedEvent();
            }
            return true;
        }
        return false;
    }

    /**
     * Get string format for the _value which can be displayed in input and edited by user 
     */
    public abstract convertValueToInputValue(value: T | undefined): string;

    /**
     * Decorate the input depending on input value validity
     * @returns false if value is undefined and input is not empty
     */
    protected override _validateInputValue(value: T | undefined): boolean {
        if (value === undefined) {
            // undefined is valid if empty string
            const inputString = this.getInput().input.value;
            if (!inputString?.length) {
                this.setValidationError(undefined);// clear error
                return true;
            }
            this.setValidationError(this._validation.getDefaultValidationError(inputString ?? ""));
            return false;
        }
        this.setValidationError(undefined);// clear error
        return true;
    }

    /**
     * Sets _value from input value and triggers onValueChange if value changed
     * Is triggered by editor when input value changes
     */
    protected override _setValueIfDifferentAndDispatchEvents(newValue: T | undefined): boolean {
        const changed = super._setValueIfDifferentAndDispatchEvents(newValue);
        if (newValue != null) {
            // value may not change but we may need to reformat input value
            this._refreshInputValue();
        }
        return changed;
    }

    /**
     * Update the value of the input from _value of the Editor
     */
    protected _refreshInputValue(): void {
        const inputValue = this.convertValueToInputValue(this._value);

        this._inputWithButton.setValue(inputValue);
        if (this._inputWithButton.isInitDone) {
            // if !isInitDone refresh will be called later
            this._inputWithButton.refresh();
        }

    }

    /** Update disabled attribute on input component */
    protected _refreshDisabled(): void {
        const disabledValue = this.disabled;
        this.getInput().options.setAndRefreshIfChanged({
            disabled: disabledValue
        });
        this._picker?.options.setAndRefreshIfChanged({ disabled: disabledValue });
    }

    /**
     * Instantiate picker on first call, and refresh picker based on value and options (called before showPopup)
     * @returns the picker (this ensures it was instantiated)
     */
    protected abstract _refreshPicker(): TPicker;

    /**
     * Click handler when click on the button of the InputWithBtn
     * TODO: AMT - Gérer la taille du popup afin que celui-ci ait la taille maximal du picker
     */
    protected _onClickEditorBtn(e?: Event): void {

        const picker = this._refreshPicker();
        // Popup parameters
        const popupParams: ShowPopupParams = {
            elementToShow: picker,
            parent: this.options.popupContainer ?? this,
            event: e,
            onPopupHide: () => this._onPopupHide(),
            onPopupCancel: () => this._onPopupCancel(),
            popupPosition: this.options.pickerPosition ?? PopupPosition.BOTTOM_END,
            popupMode: this.options.popupMode ?? "default",
            ...this.options.popupOptions
        };

        // If other popupContainer is given, popup will be displayed in document body, with position absolute 
        if (this.options.popupContainer != null) {
            const pickerBounds = this.getBoundingClientRect();
            popupParams.absolutePosition = {
                top: pickerBounds.top,
                left: pickerBounds.left
            };
        }

        e?.stopPropagation();

        // Keep value before opening popup
        this._beforeOpenPopupValue = clone(this._value);// Remove reference, some pickers only update objects fields

        // Open popup
        this._popupContainer = showPopup(popupParams);
        // direct mode => auto hide popup on picker value change
        if (this.options.popupMode === "direct") {

            // Remove footer of CjsDialog if popupElement is already in an other CjsDialog
            if (isInModal(picker) && this._popupContainer.popup instanceof CjsDialog) {
                this._popupContainer.popup.options.setAndRefreshIfChanged({
                    hideSections: ["header", "footer"]
                });
            }

            // Add listener on value change event of popupComponent (picker)
            picker.valueChangeEvent.on(() => {
                if (this._popupContainer) {
                    // Close popup and enable toggleBtn
                    this._popupContainer.removePopup("close");
                    this._inputWithButton.setDisabled(false);
                }
            }, { once: true });// listener will be removed after the event is triggered
        }

        if (this.options.enableChangeValueWithPicker) {
            // Add listener on value change event of popupComponent (picker)
            picker.valueChangeEvent.on(() => {
                this._setValueIfDifferentAndDispatchEvents(this._getPickerValue());
            });
        }

        this._inputWithButton.setDisabled(true);
        this._afterButtonClick();
    }

    /** Callback called after inputWithButton click */
    protected _afterButtonClick(): void {
        // Nothing todo for default EditorWithPicker
    }

    /** Callback called after popup will hide and cancelled */
    protected _afterPopupHideOrCancel(): void {
        // Nothing todo for default EditorWithPicker
    }

    /** When the popup goes to hide */
    protected _onPopupHide(): void {

        setTimeout(() => {// Timeout value change to enable other sync events to be dispatched
            if (!this.options.enableChangeValueWithPicker) {
                this._setValueIfDifferentAndDispatchEvents(this._getPickerValue());
            } else {
                // only reformat value in input, don't need to setValue here, already made by picker
                this._refreshInputValue();
            }
        });

        this._inputWithButton.setDisabled(false);

        // Reset stored value
        this._beforeOpenPopupValue = undefined;
        this._afterPopupHideOrCancel();
    }

    /**
     * @returns the picker value
     */
    protected _getPickerValue(): T | undefined {
        if (!this._picker) {
            this._log?.error("You should not call _getPickerValue before picker is initialized");
        }
        return this._picker?.getValue();
    }

    /** When the popup is cancel */
    protected _onPopupCancel(): void {
        this._inputWithButton.setDisabled(false);
        this._afterPopupHideOrCancel();

        // Reset value if needed
        if (this.options.enableChangeValueWithPicker) {
            this._setValueIfDifferentAndDispatchEvents(this._beforeOpenPopupValue);

            // Reset stored value
            this._beforeOpenPopupValue = undefined;
        }
    }

}